import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatPHP, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { X } from "lucide-react";

const STATUSES = ["pending", "confirmed", "processing", "shipped", "completed", "cancelled"];

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success("Status updated");
      load();
      if (open?.id === id) setOpen({ ...open, status });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="overline mb-2">Orders</div>
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-6">Manage Orders</h1>

      <div className="bg-white border border-msr-line overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-msr-mist text-left text-[11px] uppercase tracking-wider text-msr-slate">
            <tr>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-msr-slate" data-testid="admin-orders-empty">No orders yet.</td></tr>
            )}
            {items.map((o) => (
              <tr key={o.id} className="border-t border-msr-line" data-testid={`admin-order-row-${o.reference}`}>
                <td className="px-4 py-3 font-mono text-xs text-msr-blue">{o.reference}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{o.full_name}</div>
                  <div className="text-xs text-msr-slate">{o.email}</div>
                </td>
                <td className="px-4 py-3 font-semibold">{formatPHP(o.total)}</td>
                <td className="px-4 py-3 capitalize text-msr-slate text-xs">{o.payment_method.replace("_", " ")}</td>
                <td className="px-4 py-3">
                  <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} className="border border-msr-line bg-white text-xs px-2 py-1.5" data-testid={`admin-order-status-${o.reference}`}>
                    {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-msr-slate">{formatDate(o.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOpen(o)} className="text-msr-blue text-xs" data-testid={`admin-order-view-${o.reference}`}>View →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-40 bg-black/60 grid place-items-center p-4" data-testid="admin-order-detail">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto border-t-4 border-msr-blue">
            <div className="border-b border-msr-line px-6 py-4 flex justify-between">
              <div>
                <div className="overline">Order</div>
                <div className="font-heading font-bold text-lg">{open.reference}</div>
              </div>
              <button onClick={() => setOpen(null)} className="p-2"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Info label="Customer" value={`${open.full_name}\n${open.email}\n${open.phone}`} />
                <Info label="Company / Address" value={`${open.company || "—"}\n${open.address}\n${open.city}`} />
                <Info label="Payment" value={open.payment_method.replace("_", " ")} />
                <Info label="Status" value={open.status} />
                <Info label="Date" value={formatDate(open.created_at)} />
                <Info label="Notes" value={open.notes || "—"} />
              </div>
              <div>
                <div className="overline mb-3">Items</div>
                <table className="w-full text-sm border border-msr-line">
                  <thead className="bg-msr-mist text-left text-[11px] uppercase tracking-wider text-msr-slate">
                    <tr>
                      <th className="px-3 py-2">Item</th>
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Qty</th>
                      <th className="px-3 py-2 text-right">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {open.items.map((i, idx) => (
                      <tr key={idx} className="border-t border-msr-line">
                        <td className="px-3 py-2">{i.name}</td>
                        <td className="px-3 py-2 text-msr-slate">{i.sku}</td>
                        <td className="px-3 py-2">{i.quantity}</td>
                        <td className="px-3 py-2 text-right font-semibold">{formatPHP(i.price * i.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-msr-line">
                <span className="font-heading font-extrabold">Total</span>
                <span className="font-heading font-extrabold text-2xl text-msr-blue">{formatPHP(open.total)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm font-semibold whitespace-pre-line text-msr-ink">{value}</div>
    </div>
  );
}
